import { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';

interface BrandLogo {
  brandName: string;
  logoUrl: string | null;
  domain?: string;
  source?: string;
}

interface UseBrandLogosReturn {
  logos: Map<string, string>;
  fetchLogo: (brandName: string, domain?: string) => Promise<string | null>;
  fetchBulkLogos: (brands: Array<{ name: string; domain?: string }>) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const logoCache = new Map<string, string>();

export const useBrandLogos = (): UseBrandLogosReturn => {
  const [logos, setLogos] = useState<Map<string, string>>(new Map(logoCache));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCacheKey = (brandName: string, domain?: string) => {
    return `${brandName.toLowerCase()}-${domain || 'no-domain'}`;
  };

  const fetchLogo = useCallback(async (brandName: string, domain?: string): Promise<string | null> => {
    const cacheKey = getCacheKey(brandName, domain);

    // Check cache first
    if (logoCache.has(cacheKey)) {
      return logoCache.get(cacheKey) || null;
    }

    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (domain) {
        params.append('domain', domain);
      }

      const response = await apiClient.get(`/backend/api/admin/brands/${encodeURIComponent(brandName)}/logo?${params.toString()}`);

      if (response.data.success && response.data.data.logoUrl) {
        const logoUrl = response.data.data.logoUrl;

        // Update cache
        logoCache.set(cacheKey, logoUrl);
        setLogos(new Map(logoCache));

        return logoUrl;
      } else {
        // Cache null result to avoid repeated requests
        logoCache.set(cacheKey, '');
        setLogos(new Map(logoCache));
        return null;
      }
    } catch (err: any) {
      console.error('Error fetching logo:', err);
      setError(err.message || 'Failed to fetch logo');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchBulkLogos = useCallback(async (brands: Array<{ name: string; domain?: string }>) => {
    try {
      setIsLoading(true);
      setError(null);

      // Filter out brands that are already cached
      const uncachedBrands = brands.filter(brand => {
        const cacheKey = getCacheKey(brand.name, brand.domain);
        return !logoCache.has(cacheKey);
      });

      if (uncachedBrands.length === 0) {
        return; // All logos are already cached
      }

      const response = await apiClient.post('/backend/api/admin/brands/logos/bulk', {
        brands: uncachedBrands
      });

      if (response.data.success) {
        const logoResults: BrandLogo[] = response.data.data;
        
        logoResults.forEach(result => {
          const cacheKey = getCacheKey(result.brandName, result.domain);
          logoCache.set(cacheKey, result.logoUrl || '');
        });

        setLogos(new Map(logoCache));
      }
    } catch (err: any) {
      console.error('Error fetching bulk logos:', err);
      setError(err.message || 'Failed to fetch bulk logos');
    } finally {
      setIsLoading(false);
    }
  }, []);



  // Pre-populate with known platform logos
  useEffect(() => {
    const platformLogos = {
      'platform-shopify': 'https://logo.clearbit.com/shopify.com',
      'platform-judge.me': 'https://logo.clearbit.com/judge.me',
      'platform-judgeme': 'https://logo.clearbit.com/judge.me',
      'platform-yotpo': 'https://logo.clearbit.com/yotpo.com',
      'platform-instagram': 'https://logo.clearbit.com/instagram.com',
      'platform-facebook': 'https://logo.clearbit.com/facebook.com'
    };

    Object.entries(platformLogos).forEach(([key, url]) => {
      if (!logoCache.has(key)) {
        logoCache.set(key, url);
      }
    });

    setLogos(new Map(logoCache));
  }, []);

  return {
    logos,
    fetchLogo,
    fetchBulkLogos,
    isLoading,
    error
  };
};

// Utility function to get logo URL from cache
export const getLogoFromCache = (brandName: string, domain?: string): string | null => {
  const cacheKey = `${brandName.toLowerCase()}-${domain || 'no-domain'}`;
  return logoCache.get(cacheKey) || null;
};

// Utility function to get platform logo from cache
export const getPlatformLogoFromCache = (platformName: string): string | null => {
  const cacheKey = `platform-${platformName.toLowerCase()}`;
  return logoCache.get(cacheKey) || null;
};
